import { workTypeMeta } from "./workTypeModel";

export const IMPORTANCE_LABELS = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};

export const URGENCY_LABELS = {
  immediate: "Immediate",
  soon: "Soon",
  flexible: "Flexible",
  unknown: "Not answered",
};

const DELIVERY_TO_FOUNDER = {
  idea: { code: "still_planning", label: "Still being planned" },
  planned: { code: "still_planning", label: "Still being planned" },
  ready_for_brief: { code: "still_planning", label: "Still being planned" },
  brief_ready: { code: "ready_to_build", label: "Ready to build" },
  in_progress: { code: "in_delivery", label: "In delivery" },
  in_review: { code: "in_review", label: "In review" },
  accepted: { code: "completed", label: "Completed" },
  blocked: { code: "blocked", label: "Blocked" },
  deferred: { code: "archived", label: "Deferred" },
};

const AGENT_STATE_TO_FOUNDER = {
  needs_product_input: { code: "needs_decision", label: "Needs a decision" },
  ready_for_engineering: { code: "ready_to_build", label: "Ready to build" },
  available_to_agents: { code: "in_engineering", label: "In engineering" },
  working_now: { code: "in_engineering", label: "In engineering" },
  needs_your_answer: { code: "needs_decision", label: "Needs a decision" },
  ready_for_review: { code: "in_review", label: "In review" },
  accepted: { code: "completed", label: "Completed" },
  paused: { code: "blocked", label: "Paused" },
  expired: { code: "blocked", label: "Blocked" },
  not_available: null,
};

/**
 * Founder-facing readiness for Roadmap / priority surfaces.
 * FeaturePlan generation status must never be treated as engineering completion.
 */
export function founderReadinessFromRoadmapItem(item) {
  const delivery = String(item?.deliveryStatus || "").toLowerCase();
  const agentState = item?.agentExecution?.customerState || null;
  const hasReview = Boolean(item?.linkedAcceptanceReview?.id);
  const reviewStatus = String(item?.linkedAcceptanceReview?.status || "").toLowerCase();
  const blocker = String(item?.blocker || "").trim();

  if (delivery === "accepted" || agentState === "accepted" || reviewStatus === "accepted") {
    return { code: "completed", label: "Completed" };
  }
  if (delivery === "deferred") {
    return { code: "archived", label: "Deferred" };
  }
  if (delivery === "blocked" || blocker || agentState === "paused" || agentState === "expired") {
    return { code: "blocked", label: "Blocked" };
  }
  // Any linked Acceptance Review means this Task has left "ready to build" — match Tasks home.
  if (
    hasReview ||
    delivery === "in_review" ||
    agentState === "ready_for_review" ||
    ["in_review", "changes_requested", "needs_changes", "completed", "stale"].includes(reviewStatus)
  ) {
    if (["changes_requested", "needs_changes"].includes(reviewStatus)) {
      return { code: "in_review", label: "Changes requested" };
    }
    return { code: "in_review", label: "In review" };
  }
  if (hasReview && delivery === "in_progress") {
    return { code: "in_delivery", label: "Delivery connected" };
  }
  if (agentState === "working_now" || agentState === "available_to_agents") {
    return { code: "in_engineering", label: "In engineering" };
  }
  if (delivery === "in_progress") {
    return { code: "in_delivery", label: "In delivery" };
  }
  if (agentState === "needs_product_input" || agentState === "needs_your_answer") {
    return { code: "needs_decision", label: "Needs a decision" };
  }
  if (DELIVERY_TO_FOUNDER[delivery]) {
    return DELIVERY_TO_FOUNDER[delivery];
  }
  if (AGENT_STATE_TO_FOUNDER[agentState]) {
    return AGENT_STATE_TO_FOUNDER[agentState];
  }
  return { code: "readiness_unknown", label: "Readiness unknown" };
}

/** Founder-facing label for a raw deliveryStatus enum (form options / badges). */
export function deliveryStatusLabel(status) {
  const key = String(status || "").toLowerCase();
  if (DELIVERY_TO_FOUNDER[key]) return DELIVERY_TO_FOUNDER[key].label;
  return "Readiness unknown";
}

export function importanceLabel(value) {
  const key = String(value || "").toLowerCase();
  return IMPORTANCE_LABELS[key] || "Medium";
}

export function urgencyLabel(value) {
  const key = String(value || "").toLowerCase();
  return URGENCY_LABELS[key] || "Not answered";
}

export function workTypeDisplay(item) {
  if (!item?.workType) {
    return { value: "", label: "Needs a type", icon: "•", needsType: true };
  }
  const meta = workTypeMeta(item.workType);
  return {
    value: item.workType,
    label: item.workTypeLabel || meta.label,
    icon: meta.icon,
    needsType: false,
    suggestedByAi: Boolean(item.workTypeSuggestedByAi),
  };
}

export function agentWorkflowSummary(item) {
  const execution = item?.agentExecution || {};
  const state = execution.customerState;
  if (!state || state === "not_available") {
    return {
      visible: false,
      line: "Not currently available to agents.",
      nextActor: null,
    };
  }
  const meaningfulNext = ["you", "engineering", "agent", "reviewer"].includes(execution.nextActor);
  return {
    visible: true,
    line: execution.customerStateLabel || "Agent workflow in progress",
    nextActor: meaningfulNext ? execution.nextActorLabel : null,
    available: Boolean(execution.availableToAgents),
  };
}

export function reviewSummaryForCollapsed(item) {
  const readiness = founderReadinessFromRoadmapItem(item);
  if (!item?.linkedAcceptanceReview?.id) {
    if (["still_planning", "ready_to_build", "needs_decision", "readiness_unknown"].includes(readiness.code)) {
      return null;
    }
  }
  if (readiness.code === "in_review") return readiness.label;
  if (readiness.code === "in_delivery") return "Delivery connected";
  if (readiness.code === "completed") return "Accepted";
  return null;
}

export function whyOnRoadmap(item, position) {
  const readiness = founderReadinessFromRoadmapItem(item);
  const importance = importanceLabel(item?.priority);
  if (readiness.code === "blocked") {
    return item?.blocker
      ? `Why #${position}: Blocked — ${item.blocker}`
      : `Why #${position}: Blocked until a product or engineering question is settled.`;
  }
  if (readiness.code === "needs_decision") {
    return `Why #${position}: Waiting on one product decision.`;
  }
  if (readiness.code === "in_engineering" || readiness.code === "in_delivery") {
    return `Why #${position}: Work is already underway.`;
  }
  if (readiness.code === "in_review") {
    return `Why #${position}: Delivery is waiting on Acceptance Review.`;
  }
  if (readiness.code === "completed") {
    return `Why #${position}: Finished work kept for reference.`;
  }
  return `Why #${position}: ${importance} importance and ${readiness.label.toLowerCase()}.`;
}
